import React, { useState, useEffect } from "react";
import { Panel, useStore } from "@xyflow/react";
import axios from "axios";
import { Button, Drawer, Textarea, TextInput, XClose } from "@opendate-app/setlist";

import { useCustomCampaignShowContext } from "./CustomCampaignShowContainer";
import { toastSuccess, toastError } from "../../../shared/toastHelper";

const surfaceStyle = {
  backgroundColor: "var(--sl-bg-primary)",
  border: "1px solid var(--sl-border-secondary)",
  boxShadow: "var(--sl-shadow-sm)",
};

const sidebarStyle = {
  ...surfaceStyle,
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  gap: 4,
  padding: "12px 24px",
  position: "relative",
  width: 280,
  boxSizing: "border-box",
};

const summaryStyle = {
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  gap: 4,
  width: "100%",
  padding: 0,
  border: 0,
  background: "none",
  textAlign: "left",
  cursor: "pointer",
  font: "inherit",
};

const nameStyle = {
  fontSize: 14,
  lineHeight: "20px",
  fontWeight: 600,
  color: "var(--sl-text-primary)",
};

const descriptionStyle = {
  fontSize: 12,
  lineHeight: "18px",
  color: "var(--sl-text-quaternary)",
};

const editingPanelStyle = {
  ...surfaceStyle,
  display: "flex",
  flexDirection: "column",
  gap: 16,
  borderRadius: "0 12px 12px 0",
  left: 278,
  top: -1,
  padding: 24,
  position: "absolute",
  width: 400,
  zIndex: 1000,
  boxSizing: "border-box",
};

const drawerFormStyle = {
  display: "flex",
  flexDirection: "column",
  gap: 16,
};

const editingHeaderStyle = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  fontSize: 16,
  lineHeight: "24px",
  fontWeight: 600,
  color: "var(--sl-text-primary)",
};

export default () => {
  const {
    csrfToken,
    customCampaign,
    setCustomCampaign,
    customCampaignBaseUrl,
  } = useCustomCampaignShowContext();

  const [isEditing, setIsEditing] = useState(false);
  const [name, setName] = useState(customCampaign.name);
  const [description, setDescription] = useState(customCampaign.description || "");
  const [faIcon, setFaIcon] = useState(customCampaign.fa_icon || "");
  const [saveDisabled, setSaveDisabled] = useState(true);

  const isTemplate = customCampaign.type === "CustomCampaigns::Template";
  const narrowCanvas = useStore((state) => state.width < 720);
  const [stacked, setStacked] = useState(false);

  const openEditor = () => {
    if (isEditing) {
      return;
    }

    setName(customCampaign.name);
    setDescription(customCampaign.description || "");
    setFaIcon(customCampaign.fa_icon || "");
    setStacked(narrowCanvas);
    setIsEditing(true);
  };

  useEffect(() => {
    if (isEditing && narrowCanvas) {
      setStacked(true);
    }
  }, [isEditing, narrowCanvas]);

  useEffect(() => {
    setSaveDisabled(
      !name || (
        name === customCampaign.name &&
        description === (customCampaign.description || "") &&
        faIcon === (customCampaign.fa_icon || "")
      )
    );
  }, [name, description, faIcon, isEditing]);

  const updateCampaign = (e) => {
    e.preventDefault();

    const payload = {
      name,
      description,
      ...(isTemplate && { fa_icon: faIcon }),
    };

    axios.defaults.headers.common["X-CSRF-TOKEN"] = csrfToken;
    axios.patch(`${customCampaignBaseUrl}`, payload)
      .then(({ data }) => {
        setCustomCampaign(data);
        setIsEditing(false);
        toastSuccess("Automation updated successfully");
      })
      .catch(() => {
        toastError("Unable to update automation");
      });
  };

  const fields = (
    <>
      {isTemplate && (
        <TextInput
          label="FontAwesome icon"
          hint={<a href="https://fontawesome.com/icons" target="_blank" rel="noopener noreferrer">Browse icons</a>}
          leadingText={faIcon ? <i className={faIcon} /> : null}
          value={faIcon}
          onChange={(e) => setFaIcon(e.target.value)}
          placeholder="e.g., fa-solid fa-star"
        />
      )}
      <TextInput
        label="Automation name"
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="Enter a name..."
      />
      <Textarea
        label="Automation description"
        value={description || ""}
        onChange={(e) => setDescription(e.target.value)}
        rows={6}
        resize="none"
        placeholder="Add a description..."
      />
      <div>
        <Button variant="primary" type="submit" disabled={saveDisabled}>Save</Button>
      </div>
    </>
  );

  return (
    <Panel position="top-left">
      <div
        style={{
          ...sidebarStyle,
          borderRadius: isEditing && !stacked ? "12px 0 0 12px" : 12,
        }}
      >
        <button type="button" style={summaryStyle} onClick={openEditor}>
          <span style={nameStyle}>{customCampaign.name}</span>
          <span style={descriptionStyle}>{customCampaign.description || "Add a description..."}</span>
        </button>
        {isEditing && !stacked && (
          <form style={editingPanelStyle} onSubmit={updateCampaign}>
            <div style={editingHeaderStyle}>
              Edit automation information
              <Button
                size="sm"
                variant="tertiary"
                icon={XClose}
                aria-label="Close"
                onClick={() => setIsEditing(false)}
              />
            </div>
            {fields}
          </form>
        )}
      </div>
      {stacked && (
        <Drawer
          open={isEditing}
          onClose={() => setIsEditing(false)}
          side="bottom"
          backdrop
          title="Edit automation information"
        >
          <form style={drawerFormStyle} onSubmit={updateCampaign}>
            {fields}
          </form>
        </Drawer>
      )}
    </Panel>
  );
};
